import { useRef, useEffect } from "react";

import { toCount } from "common/utils/formatting";
import { pluralized } from "common/utils/strings";
import { Optional } from "common/utils/types";

import { sendSelectItem, useSendPostHogProductClick } from "client/analytics/utils";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchResultV2 } from "../types";

export const useScrollToItemSelection = (id: number, isActive: boolean) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<{ [id: number]: Optional<HTMLElement> }>({});

  const setRef = (key: number, ref: Optional<HTMLElement>) => {
    optionRefs.current[key] = ref;
  };

  useEffect(() => {
    // Only run when active on the page to help reset selected values to the proper place
    if (!isActive) {
      return;
    }

    const optionElement = optionRefs.current[id];
    const scrollElement = scrollRef.current;

    if (!optionElement || !scrollElement) {
      return;
    }

    const pagePadding = 16; // Same as what's set in the <Body />
    const scrollRect = scrollElement.getBoundingClientRect();
    const optionRect = optionElement.getBoundingClientRect();

    // Determine how much to scroll by based on how far the element is off the screen
    const scrollRight = Math.min(scrollRect.width - pagePadding - optionRect.right, 0) * -1;
    const scrollLeft = Math.max(pagePadding - optionRect.left, 0) * -1;
    const scrollChange = scrollLeft + scrollRight;

    // Scroll to that position
    scrollElement.scrollLeft += scrollChange;
  }, [id, isActive]);

  return {
    scrollRef,
    setRef,
  };
};

// The display fields a search result card derives from a result — shared by the desktop and
// mobile cards so they stay in sync.
export const useProductSearchCardFields = (
  result: ProductSearchResultV2,
  listSource: ProductListSource,
) => {
  const sendPostHogProductClick = useSendPostHogProductClick();

  const productLine = result.product_line;
  const hasPrescriptionLabel = Boolean(result.patient_prescription_label);
  const isFeatured = productLine.is_featured || productLine.is_loyalty_program_featured;

  const defaultItem = result.default_item;
  const packLabel =
    defaultItem.units_count != null && defaultItem.unit_label
      ? toCount(defaultItem.units_count, defaultItem.unit_label)
      : "";
  // Drop empty/missing segments so the "·" separators don't double up.
  const variantLabel = [result.primary_label, result.secondary_label].filter(Boolean).join(" · ");

  const dim = result.item_dimensions;
  const additionalCount = dim ? dim.count - 1 : 0;
  const sizesLabel = dim ? `${additionalCount} more ${pluralized(additionalCount, dim.label)}` : "";
  const flavorsLabel =
    defaultItem.flavor_count != null ? `${defaultItem.flavor_count} flavors` : "";
  const optionsLabel = [sizesLabel, flavorsLabel].filter(Boolean).join(" · ");

  // Compounds show "Compounded" as the first fact chip rather than a separate icon treatment.
  const facts = productLine.is_compound
    ? [{ label: "Compounded", value: "Compounded" }, ...result.card_facts]
    : result.card_facts;

  const handleClick = () => {
    sendSelectItem(result, listSource);
    sendPostHogProductClick({
      source: listSource,
      id: result.id,
      product_line_id: result.product_line.id,
      default_item_id: result.default_item.id,
    });
  };

  return {
    productLine,
    hasPrescriptionLabel,
    isFeatured,
    variantLabel,
    packLabel,
    optionsLabel,
    facts,
    handleClick,
  };
};
